/**
 * G: Explorer - the pivot engine. Cut the reconciled WiP ledger by
 * any dimension x period grain x metric; heatmap matrix + trend
 * chart; every cell drills to the underlying invoices.
 */
import { useMemo, useState } from "react";
import { fmtGbpShort, PanelTitle } from "../staff/CommissionedPanels";
import { useCfoData, TabState, Card, SourceBadge } from "./cfoShared";
import ExplorerMatrix from "./ExplorerMatrix";
import ExplorerDrill from "./ExplorerDrill";
import { PALETTE, DIMENSIONS, GRAINS, METRICS, BASES, ADDITIVE, metricValue, fmtPeriod } from "./explorerUtil";

function Seg({ label, options, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[9px] font-mono uppercase tracking-wider text-stone-400 w-16 shrink-0">{label}</span>
      <div className="flex flex-wrap gap-1" data-testid={testid}>
        {options.map((o) => (
          <button
            key={o.id} type="button" onClick={() => onChange(o.id)}
            data-testid={`${testid}-${o.id}`}
            className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
              value === o.id
                ? "bg-[#1B4F8A] border-[#1B4F8A] text-white"
                : "bg-white border-stone-200 text-stone-600 hover:border-stone-400"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ExplorerTab() {
  const [dimension, setDimension] = useState("team");
  const [grain, setGrain] = useState("quarter");
  const [metric, setMetric] = useState("revenue");
  const [scheduled, setScheduled] = useState(false);
  const [basis, setBasis] = useState("all");
  const [drill, setDrill] = useState(null);

  const { data, loading, error } = useCfoData(
    `cfo/explorer?dimension=${dimension}&grain=${grain}&scheduled=${scheduled ? 1 : 0}&basis=${basis}`,
  );

  const additive = ADDITIVE.has(metric);
  const chart = useMemo(() => {
    if (!data) return { points: [], series: [] };
    const top = data.rows.filter((r) => r.key !== "__others__").slice(0, additive ? 8 : 6);
    const rest = data.rows.filter((r) => !top.includes(r));
    const points = data.periods.map((p) => {
      const pt = { period: fmtPeriod(p) };
      for (const r of top) pt[r.label] = metricValue(r.cells[p], metric);
      if (additive && rest.length) {
        pt["Others"] = rest.reduce((s, r) => s + (metricValue(r.cells[p], metric) || 0), 0) || null;
      }
      return pt;
    });
    const series = top.map((r) => r.label);
    if (additive && rest.length) series.push("Others");
    return { points, series };
  }, [data, metric, additive]);

  const dimLabel = DIMENSIONS.find((d) => d.id === dimension)?.label || dimension;
  const fmtVal = (v) => (metric === "margin" ? `${Math.round(v)}%` : fmtGbpShort(v));

  return (
    <div className="flex flex-col gap-4" data-testid="cfo-explorer">
      <Card testid="explorer-controls">
        <div className="flex flex-col gap-2.5">
          <Seg label="Cut by" options={DIMENSIONS} value={dimension} onChange={setDimension} testid="explorer-dim" />
          <Seg label="Grain" options={GRAINS} value={grain} onChange={setGrain} testid="explorer-grain" />
          <Seg label="Metric" options={METRICS} value={metric} onChange={setMetric} testid="explorer-metric" />
          <Seg label="GP basis" options={BASES} value={basis} onChange={setBasis} testid="explorer-basis" />
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-mono uppercase tracking-wider text-stone-400 w-16 shrink-0">Scope</span>
            <button
              type="button" onClick={() => setScheduled((s) => !s)}
              data-testid="explorer-scheduled-toggle"
              className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                scheduled ? "bg-sky-50 border-sky-300 text-sky-800" : "bg-white border-stone-200 text-stone-600 hover:border-stone-400"
              }`}
            >
              {scheduled ? "Billed + scheduled" : "Billed only"}
            </button>
            <span className="text-[10px] text-stone-400">
              {scheduled ? "includes future-dated instalments" : "instalments dated before today"}
            </span>
          </div>
        </div>
      </Card>

      {(loading || error) && <TabState loading={loading} error={error} testid="cfo-explorer" />}

      {data && !loading && (
        <>
          <Card testid="explorer-matrix-card">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
              <PanelTitle>
                {METRICS.find((m) => m.id === metric)?.label} by {dimLabel.toLowerCase()} · per {grain}
                {data.row_count > data.rows.length ? ` · top ${data.rows.length - 1} of ${data.row_count}` : ""}
              </PanelTitle>
              <SourceBadge kind={dimension === "qual_quant" ? "joined" : "wip"} />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="explorer-legend">
                {chart.series.map((s, i) => (
                  <span key={s} className="inline-flex items-center gap-1.5 text-[10px] text-stone-500">
                    <span
                      className="w-2.5 h-2.5 rounded-sm inline-block"
                      style={{ backgroundColor: PALETTE[i % PALETTE.length] }}
                    />
                    {s}
                  </span>
                ))}
              </div>
              <span className="text-[10px] text-stone-400">Click a cell for the underlying invoices</span>
            </div>
            <ExplorerMatrix
              data={data}
              metric={metric}
              dimLabel={dimLabel}
              chart={chart}
              additive={additive}
              fmtVal={fmtVal}
              onCell={(row, period) => setDrill({
                dimension, key: row.key, label: row.label, period, grain, scheduled, basis,
              })}
            />
            {!additive && (
              <p className="text-[10px] text-stone-400 mt-2">
                Ratio metrics are shown as lines for the top {chart.series.length} by revenue - they don't stack.
              </p>
            )}
          </Card>
        </>
      )}

      {drill && <ExplorerDrill query={drill} onClose={() => setDrill(null)} />}
    </div>
  );
}
